import type { LevelConfig, LevelPiece } from "../level_config.js";

// Same numeric value as PlayDialogDifficulty.Common — kept as a literal
// instead of importing play_dialog.ts, same leaf-module reasoning as the
// other showcases' local COMMON_DIFFICULTY.
const COMMON_DIFFICULTY = 0;

/**
 * Static test level for the hang rig (see hanging_rig.ts, physics_table.ts
 * #createHangRig / #setHangPivot and table_swinger.ts): three legless tabletops
 * hung one above another from a SINGLE pair of placeholder cables, swaying
 * gently left and right with a stack of blocks riding on each.
 *
 *   - each tabletop is otherwise the same model every table uses, just with no
 *     leg under it;
 *   - the two red lines stand in for cable art that does not exist yet. They
 *     run up to wherever the top edge of the screen is, recomputed as the
 *     camera moves, so where they actually begin never comes into view;
 *   - the sway is a pendulum about ONE point above the topmost tabletop, so
 *     the three hold their spacing and the lowest one — furthest from that
 *     point — sweeps the widest arc;
 *   - that point sits at anchorHeight, nowhere near the top of the drawn
 *     cable: tying the sway to where the line ends would scale the swept
 *     distance with the screen instead of keeping it a gentle sway;
 *   - the blocks are carried through the same motion every physics step
 *     (TableSwinger), the way FieldRotator carries blocks around a spinning
 *     table.
 *
 * standOn is what stacks them: it is the existing "place me above table N,
 * clear of its deck" rule, and it is worth reading literally here — the leg it
 * would normally imply is the one the hang rig throws away.
 */
const COLS = 8;
const ROWS = 4;

function makeDeck(): { grid: number[][]; pieces: LevelPiece[] }
{
  const grid: number[][] = [];
  const pieces: LevelPiece[] = [];
  let id = 1;

  for ( let row = 0; row < ROWS; row++ )
  {
    const grid_row: number[] = [];

    for ( let col = 0; col < COLS; col++ )
    {
      grid_row.push( id );
      pieces.push( { id, type: 1, x: col, y: row, width: 1, height: 1 } );
      id++;
    }

    grid.push( grid_row );
  }

  return { grid, pieces };
}

const HANGING_TABLE_LEVEL: LevelConfig = {
  level: 90306,
  difficulty: COMMON_DIFFICULTY as LevelConfig["difficulty"],
  tables: [
    { layers: [ makeDeck() ], slot: { u: 0, v: 0 }, shape: "round", hang: true },
    { layers: [ makeDeck() ], slot: { u: 0, v: -1 }, shape: "round", hang: true },
    // { layers: [ makeDeck() ], slot: { u: 0, v: 0 }, standOn: 1 },
  ],
};

export class HangingTableShowcase
{
  public static readonly LEVEL = HANGING_TABLE_LEVEL.level;

  private static active = false;

  public static isActive(): boolean
  {
    return this.active;
  }

  public static activate(): number
  {
    this.active = true;
    return this.LEVEL;
  }

  public static deactivate(): void
  {
    this.active = false;
  }

  public static getConfig( level: number ): LevelConfig | null
  {
    return level === this.LEVEL ? HANGING_TABLE_LEVEL : null;
  }
}
